import { Box, HStack, Text } from '@chakra-ui/react';
import { BaseTooltip, useStandardToast } from '@invara-dev/react-base';

type CopyableTextProps = {
  /** The text value to display and copy to clipboard */
  value: string;
  /** The descriptive label to show before the value (e.g., "Hotel", "City") */
  label: string;
  /** Optional icon component to display before the label */
  icon?: React.ReactNode;
};

/**
 * A reusable component that displays labeled text with click-to-copy functionality.
 * Shows an icon, label, and value with hover effects and tooltip instructions.
 *
 * @param props - The component props
 * @returns A clickable text element with copy functionality
 *
 * @example
 * ```tsx
 * <CopyableText
 *   value="Marriott Hotel"
 *   label="Hotel"
 *   icon={<BaseIcon icon={faBuilding} nonInteractive />}
 * />
 * ```
 */
export const CopyableText = ({ value, label, icon }: CopyableTextProps) => {
  const toast = useStandardToast();

  /**
   * Copies the value to clipboard and shows a success toast notification
   */
  const copyToClipboard = () => {
    if (!value) return;
    navigator.clipboard.writeText(value).then();
    toast.successToast('Copied to clipboard', `${label} has been copied to your clipboard.`);
  };

  return (
    <BaseTooltip content={`Click to copy ${label}: ${value}`}>
      <HStack cursor='pointer' onClick={copyToClipboard} _hover={{ bg: 'gray.50' }} borderRadius='md' p={1} gap={2}>
        {icon && (
          <Box color='gray.500' fontSize='sm'>
            {icon}
          </Box>
        )}
        <Text fontWeight='bold' fontSize='sm' color='gray.700'>
          {label}:
        </Text>
        <Text fontSize='sm'>{value}</Text>
      </HStack>
    </BaseTooltip>
  );
};
